import React from 'react';

import Icon from '../../Icon/Icon';
import TimelineMonitor from '../../inputs/Timeline/TimelineMonitor';
import { ChannelDetails, RecordableChannel } from './viewModels';

interface Props {
  channelDetailsById: Record<string, ChannelDetails>;
  channelSelections: Record<string, boolean>;
  onEditSlot: (channelId: string) => void;
  onRecordChannel: (channelId: string) => void;
  onStopChannel: (recordId: string) => void;
  onToggleChannelSelection: (channelId: string, checked: boolean) => void;
  recordableChannels: RecordableChannel[];
  slotsWithoutActiveConfig: Set<string>;
}

const RecorderGalleryView: React.FC<Props> = ({
  channelDetailsById,
  channelSelections,
  onEditSlot,
  onRecordChannel,
  onStopChannel,
  onToggleChannelSelection,
  recordableChannels,
  slotsWithoutActiveConfig,
}) => {
  const getBorderColor = (status?: string) => {
    switch ((status ?? '').toLowerCase()) {
      case 'conflict':
        return '#da1e28';
      case 'paused':
        return '#f1c21b';
      case 'recording':
        return '#24a148';
      case 'stand-by':
        return '#8d8d8d';
      default:
        return '#8d8d8d';
    }
  };

  return (
    <div style={{ display: 'flow-root' }}>
      {recordableChannels.map((channel) => {
        const details = channelDetailsById[channel.id];
        const activeItem = details?.activeItem;

        const missingConfig = slotsWithoutActiveConfig.has(channel.id);
        const isDisabled = channel.enabled === false;
        const cannotRecord = missingConfig || isDisabled;

        return (
          <div
            key={`gallery_${channel.id}`}
            style={{
              float: 'left',
              width: 200,
              marginRight: 16,
              marginBottom: 16,
              borderWidth: missingConfig ? 2 : 1,
              borderStyle: 'solid',
              borderColor: missingConfig ? '#f1c21b' : getBorderColor(details?.status),
              borderRadius: 8,
              padding: 2,
              backgroundColor: '#262626',
              opacity: isDisabled ? 0.6 : 1,
            }}
          >
            <div
              style={{
                width: '100%',
                aspectRatio: '16 / 9',
                backgroundColor: '#111',
                borderRadius: 2,
                overflow: 'hidden',
                marginBottom: 10,
              }}
            >
              {channel.monitorPreviewURL ? <TimelineMonitor src={channel.monitorPreviewURL} type={channel.monitorPreviewType} /> : null}
            </div>
            {missingConfig && (
              <div style={{ fontSize: 11, color: '#f1c21b', padding: '2px 6px 4px', display: 'flex', alignItems: 'center', gap: 4 }}>
                <Icon type="warning" color="#f1c21b" size={12} />
                No default config — cannot record
              </div>
            )}
            {isDisabled && (
              <div style={{ fontSize: 11, color: '#8d8d8d', padding: '2px 6px 4px', display: 'flex', alignItems: 'center', gap: 4 }}>
                <Icon type="control_pause" color="#8d8d8d" size={12} />
                Slot disabled
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
              <input
                type="checkbox"
                checked={channelSelections[channel.id] ?? false}
                disabled={isDisabled}
                onChange={(event) => onToggleChannelSelection(channel.id, event.target.checked)}
              />
              <div style={{ flex: 1, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                {channel.title}
              </div>
              <button
                type="button"
                title="Edit slot"
                onClick={() => onEditSlot(channel.id)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center' }}
              >
                <Icon type="settings" color="#8d8d8d" size={16} />
              </button>
              <button
                type="button"
                title="Record now"
                disabled={cannotRecord}
                onClick={() => onRecordChannel(channel.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: cannotRecord ? 'not-allowed' : 'pointer',
                  padding: 4,
                  display: 'flex',
                  alignItems: 'center',
                  opacity: cannotRecord ? 0.4 : 1,
                }}
              >
                <Icon type="circle" color="#da1e28" size={16} />
              </button>
              {activeItem?.id ? (
                <button
                  type="button"
                  title="Stop"
                  onClick={() => onStopChannel(activeItem.id)}
                  style={{
                    border: '1px solid #333',
                    borderRadius: 4,
                    backgroundColor: '#da1e28',
                    color: '#fff',
                    padding: '4px 10px',
                    cursor: 'pointer',
                  }}
                >
                  Stop
                </button>
              ) : null}
            </div>
          </div>
        );
      })}
      {recordableChannels.length === 0 ? <div>No channels available for live preview.</div> : null}
    </div>
  );
};

export default RecorderGalleryView;
